/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState } from 'react'
import api, {
  FormErrors,
  IFile,
  UpdateFloorInput,
  BMDialogFloor,
  UpdateFloorAssetsVersionInput,
  CreateFloorAssetsVersionInput,
} from '@api'
import { Flex, Details, Confirmation, Button, Toaster } from '@ui'
import {
  FormGroup,
  InputGroup,
  NumericInput,
  Intent,
  ButtonGroup,
  Classes,
} from '@blueprintjs/core'
import { omit, pick, isFunction } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { useAppData } from '@providers'
import { meridianEnabled } from './utils'
import { useUser } from '@hooks'
import { sqFtToSqMeters, sqMetersToSqFt, metersToFt, ftToMeters } from '@utils'
import { fetchMeta } from '@utils/navigateToAphrodite'

const MAX_SIZE = 16
const MAX_IMAGE_WIDTH = 7000
const MAX_IMAGE_HEIGHT = 6000

interface FloorFormProps {
  dirty: boolean
  floor: BMDialogFloor
  setDirty: (dirty: boolean) => void
  onCancel: () => void
  onSubmit: (
    floor: UpdateFloorInput,
    assetVersion?: CreateFloorAssetsVersionInput | UpdateFloorAssetsVersionInput,
    shouldCallback?: boolean
  ) => Promise<any>
  buildingId?: string
  showIntegrations: boolean
  onClose: () => void
}

export type FormFloor = UpdateFloorInput

export type FloorFormErrors = FormErrors<
  FormFloor & { mapW: number; mapH: number; autocad_file: '' }
>

interface RequiredField {
  key: 'name' | 'code' | 'story'
  label: string
  error?: string
  validate?: <T extends FormFloor[keyof FormFloor]>(value: T) => boolean
}

const requiredFields: RequiredField[] = [
  { key: 'name', label: 'Name' },
  { key: 'story', label: 'Floor No.' },
  {
    key: 'code',
    label: 'Code',
    error: 'Code should contain at least 1 letter or number',
    validate(value) {
      const val = (value as string) || ''
      return /(?=.*\d)|(?=.*[a-z])|(?=.*[A-Z])/g.test(val)
    },
  },
  {
    key: 'code',
    label: 'Code',
    error: 'Maximum 80 characters',
    validate(value) {
      const val = (value as string) || ''
      return val.length <= 80 ? true : false
    },
  },
]

export default function FloorForm({
  dirty,
  setDirty,
  floor: initState,
  onCancel,
  onSubmit,
  showIntegrations,
}: FloorFormProps) {
  const { user } = useUser()
  const [floor, setFloor] = useState<Omit<FormFloor, 'story'> & { story: string }>(
    pick(
      {
        ...initState,
        areaSize: initState.areaSize ? String(initState.areaSize) : '0',
        story: String(initState.story),
      },
      [
        'name',
        'capacity',
        'code',
        'story',
        'areaSize',
        'usableArea',
        'rentableArea',
        'scale',
        'safeDistance',
        'id',
      ]
    )
  )
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<FloorFormErrors>({})
  const [localMap, setLocalMap] = useState<null | IFile>(null)
  const [localMapCad, setLocalMapCad] = useState<null | IFile>(null)
  const [mapH, setMapH] = useState(initState.assetVersion?.mapH || 0)
  const [mapW, setMapW] = useState(initState.assetVersion?.mapW || 0)
  const [meridianMap, setMeridianMap] = useState<null | { externalId: string }>(
    initState.meridianMap || null
  )
  const [dirtyRentable, setDirtyRentable] = useState(false)
  const [dirtyUsable, setDirtyUsable] = useState(false)
  const [invalidUsable, setInvalidUsable] = useState(false)
  const [dirtySize, setDirtySize] = useState(false)
  const [dirtyScale, setDirtyScale] = useState(false)
  const [dirtySafeDistance, setDirtySafeDistance] = useState(false)
  const [invalidSafeDistance, setInvalidSafeDistance] = useState(false)
  const [isOutOfRange, setIsOutOfRange] = useState(false)
  const [imageBackground, setBackground] = useState(initState?.assetVersion?.background)
  const [imageAutoCad, setAutoCad] = useState(initState?.assetVersion?.autocad)
  const assetVersion = initState.assetVersion

  const update = (field: string, val: any) => {
    setDirty && setDirty(true)
    setErrors(omit(errors, field))
    setFloor((prevState) => ({ ...prevState, [field]: val }))
  }

  function isValid(floor: FormFloor) {
    let valid = true
    requiredFields.forEach(({ key, validate }) => {
      if (valid) {
        valid = !!floor[key]
      }

      valid = valid && isFunction(validate) ? validate?.(floor[key]) : valid
    })

    return valid
  }

  const handleErrors = (error: ApolloError) => {
    setLoading(false)

    if (Array.isArray(error.graphQLErrors)) {
      const newErrors: FloorFormErrors = error.graphQLErrors.reduce(
        (result: FloorFormErrors, { invalidArguments }: any) => {
          if (!invalidArguments) return result
          return Object.assign(
            result,
            Object.entries(invalidArguments).reduce(
              (result, [key, value]) => ({
                ...result,
                [key]: value,
              }),
              {}
            )
          )
        },
        {}
      )
      setErrors(newErrors)
    }
    Toaster.fail({ error })
  }

  const highlightErrors = () => {
    const foundErrors: FloorFormErrors = {}
    requiredFields.forEach((field) => {
      if (!floor[field.key]) {
        foundErrors[field.key] = `${field.label} is required`
      }

      if (
        !foundErrors[field.key] &&
        !(isFunction(field.validate) && field.validate(floor[field.key]))
      ) {
        foundErrors[field.key] = field.error
      }
    })

    if (Object.keys(foundErrors).length) {
      setErrors((prevState) => ({ ...prevState, ...foundErrors }))
    }
  }

  const submit = () => {
    if (!isValid(floor as unknown as FormFloor)) {
      highlightErrors()
      return
    }

    setLoading(true)

    const background = localMap ? pick(localMap, ['name', 'content']) : undefined
    const autocad = localMapCad ? pick(localMapCad, ['name', 'content']) : undefined

    onSubmit(
      {
        ...floor,
        // convert unit to ft before sending if units are M
        usableArea:
          user?.context.units === 'sqm' && dirtyUsable
            ? sqMetersToSqFt(floor.usableArea ?? 0)
            : floor.usableArea,
        // convert unit to ft before sending if units are M
        rentableArea:
          user?.context.units === 'sqm' && dirtyRentable
            ? sqMetersToSqFt(floor.rentableArea ?? 0)
            : floor.rentableArea,
        // convert unit to ft before sending if units are M
        areaSize:
          user?.context.units === 'sqm' && dirtySize
            ? String(sqMetersToSqFt(Number(floor.areaSize) ?? 0))
            : floor.areaSize,
        // convert unit to ft before sending if units are M
        scale:
          user?.context.units === 'sqm' && dirtyScale ? metersToFt(floor.scale ?? 0) : floor.scale,
        // convert unit to ft before sending if units are M
        safeDistance:
          user?.context.units === 'sqm' && dirtySafeDistance
            ? metersToFt(floor.safeDistance ?? 0)
            : floor.safeDistance,

        story: Number(floor.story),
        meridianMap:
          showIntegrations && meridianMap ? { externalId: meridianMap.externalId } : null,
      },
      // @ts-ignore
      background ||
        autocad ||
        assetVersion?.mapH !== mapH ||
        mapW !== assetVersion?.mapW ||
        assetVersion?.background !== imageBackground ||
        assetVersion?.autocad !== imageAutoCad
        ? {
            versionId: initState.assetVersion?.id,
            mapH,
            mapW,
            autocad: autocad ? autocad : imageAutoCad ? undefined : null,
            background: background ? background : imageBackground ? undefined : null,
          }
        : undefined,
      true
    ).catch(handleErrors)
  }
  const removeImageAndCad = (key: string) => {
    key === 'background' ? setBackground(null) : setAutoCad(null)
    setDirty(true)
  }
  return (
    <div className={Classes.DIALOG_BODY} style={{ marginTop: 40 }}>
      <Details
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}>
        <Details.Columns>
          <Details.Column>
            <Details.Row>
              <Details.Label label="Floor Name" required />
              <FormGroup
                {...{
                  helperText: errors.name,
                  intent: errors.name ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <InputGroup
                  value={floor.name}
                  onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                    update('name', value)
                  }
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Floor No." required />
              <FormGroup
                {...{
                  helperText: errors.story,
                  intent: errors.story ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  minorStepSize={null}
                  buttonPosition="none"
                  value={floor.story}
                  onValueChange={(valueAsNumber: number, valueAsString: string) =>
                    update('story', valueAsString)
                  }
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label label={`Rentable Area (${user?.context.units})`} />
              <FormGroup
                {...{
                  helperText: errors.rentableArea,
                  intent: errors.rentableArea ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  minorStepSize={null}
                  buttonPosition="none"
                  value={
                    user?.context.units === 'sqm'
                      ? dirtyRentable
                        ? floor?.rentableArea ?? 0
                        : sqFtToSqMeters(floor?.rentableArea ?? 0)
                      : floor?.rentableArea ?? 0
                  }
                  onValueChange={(valueAsNumber: number) => {
                    update('rentableArea', valueAsNumber)
                    setDirtyRentable(true)
                  }}
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Max Capacity" />
              <FormGroup
                {...{
                  helperText: errors.capacity,
                  intent: errors.capacity ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  minorStepSize={null}
                  buttonPosition="none"
                  value={floor?.capacity ? +floor.capacity : 0}
                  onValueChange={(valueAsNumber: number) => update('capacity', valueAsNumber)}
                />
              </FormGroup>
            </Details.Row>
          </Details.Column>
          <Details.Column>
            <Details.Row>
              <Details.Label label="Code" required />
              <FormGroup
                {...{
                  helperText: isOutOfRange ? 'Maximum 80 characters' : errors.code,
                  intent: errors.code || isOutOfRange ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <InputGroup
                  value={floor.code}
                  onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                    update('code', value)
                    value.length <= 80 ? setIsOutOfRange(false) : setIsOutOfRange(true)
                  }}
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label label={`Usable Area (${user?.context.units})`} />
              <FormGroup
                {...{
                  helperText: errors.usableArea,
                  intent: (invalidUsable || errors.usableArea) && Intent.DANGER,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  minorStepSize={null}
                  buttonPosition="none"
                  value={
                    user?.context.units === 'sqm'
                      ? dirtyUsable
                        ? floor?.usableArea ?? 0
                        : sqFtToSqMeters(floor?.usableArea ?? 0)
                      : floor?.usableArea ?? 0
                  }
                  onValueChange={(valueAsNumber: number) => {
                    if (Number.isNaN(valueAsNumber) || valueAsNumber < 0) {
                      setInvalidUsable(true)
                    } else {
                      setInvalidUsable(false)
                      update('usableArea', valueAsNumber)
                      setDirtyUsable(true)
                    }
                  }}
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label label={`Area size (${user?.context.units})`} />
              <FormGroup
                {...{
                  helperText: errors.areaSize,
                  intent: errors.areaSize ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  minorStepSize={null}
                  buttonPosition="none"
                  value={
                    user?.context.units === 'sqm'
                      ? dirtySize
                        ? floor?.areaSize ?? 0
                        : sqFtToSqMeters(Number(floor?.areaSize) ?? 0)
                      : floor?.areaSize ?? 0
                  }
                  onValueChange={(valueAsNumber: number, valueAsString: string) => {
                    update('areaSize', valueAsString)
                    setDirtySize(true)
                  }}
                />
              </FormGroup>
            </Details.Row>
          </Details.Column>
        </Details.Columns>
        <Details.Header label="Map" iconName="map" />
        <Details.Columns>
          <Details.Column>
            <Details.Row>
              <Details.Label label="Floor Plan Image" required />
            </Details.Row>
            <Details.Files
              editing
              fileDropHeight={(assetVersion && imageBackground) || localMap ? 255 : 310}
              files={
                // only allowing 1 file at a time & local takes priority
                localMap ? [] : floor && assetVersion && imageBackground ? [imageBackground] : []
              }
              localFiles={localMap ? [localMap] : []}
              onAdd={(files) => {
                
                const img = new Image()
                img.onload = function () {
                  // @ts-ignore
                  if (this.width > MAX_IMAGE_WIDTH  || this.height > MAX_IMAGE_HEIGHT) {
                    Toaster.fail({ message: `Validation Error: The maximum allowed dimensions are ${MAX_IMAGE_WIDTH} X ${MAX_IMAGE_HEIGHT} pixels. Please try again.` })
                  } else {
                    setDirty(true)
                    // @ts-ignore
                    setMapW(this.width)
                    // @ts-ignore
                    setMapH(this.height)
                    setLocalMap(files[0])
                  }
                }
                img.src = files[0].content
              }}
              onRemove={localMap ? () => setLocalMap(null) : () => removeImageAndCad('background')}
              maxSize={MAX_SIZE}
            />
          </Details.Column>
          <Details.Column>
            <Details.Row>
              <Details.Label label="Width (pixels)" required />
              <FormGroup
                {...{
                  helperText: errors.mapW,
                  intent: errors.mapW ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  buttonPosition="none"
                  minorStepSize={null}
                  disabled={!localMap && !assetVersion?.background}
                  value={mapW}
                  onValueChange={(valueAsNumber: number) => {
                    setMapW(valueAsNumber)
                    setDirty(true)
                  }}
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Height (pixels)" required />
              <FormGroup
                {...{
                  helperText: errors.mapH,
                  intent: errors.mapH ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  buttonPosition="none"
                  minorStepSize={null}
                  disabled={!localMap && !assetVersion?.background}
                  value={mapH}
                  onValueChange={(valueAsNumber: number) => {
                    setMapH(valueAsNumber)
                    setDirty(true)
                  }}
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label
                label={`Scale (pixels per ${user?.context.units === 'sqm' ? 'meter' : 'foot'})`}
              />
              <FormGroup
                {...{
                  helperText: errors.scale,
                  intent: errors.scale ? Intent.DANGER : undefined,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  buttonPosition="none"
                  minorStepSize={null}
                  disabled={!localMap && !assetVersion?.background}
                  value={
                    user?.context.units === 'sqm'
                      ? dirtyScale
                        ? floor?.scale ?? 0
                        : ftToMeters(floor?.scale ?? 0, 1)
                      : floor?.scale ?? 0
                  }
                  onValueChange={(valueAsNumber: number) => {
                    update('scale', valueAsNumber)
                    setDirtyScale(true)
                  }}
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label
                label={`Safe Distance (${user?.context.units === 'sqm' ? 'm' : 'ft'})`}
                hint={
                  <div className="centered">
                    Set scale in order to enable <br /> safe distance (radius)
                  </div>
                }
              />
              <FormGroup
                {...{
                  helperText: errors.safeDistance,
                  intent: (invalidSafeDistance || errors.safeDistance) && Intent.DANGER,
                  style: { width: '100%' },
                }}>
                <NumericInput
                  minorStepSize={null}
                  buttonPosition="none"
                  disabled={(floor?.scale ?? 0) <= 0}
                  value={
                    user?.context.units === 'sqm'
                      ? dirtySafeDistance
                        ? floor?.safeDistance ?? 0
                        : ftToMeters(floor?.safeDistance ?? 0, 1)
                      : floor?.safeDistance ?? 0
                  }
                  onValueChange={(valueAsNumber: number) => {
                    if (Number.isNaN(valueAsNumber) || valueAsNumber < 0) {
                      setInvalidSafeDistance(true)
                    } else {
                      setInvalidSafeDistance(false)
                      update('safeDistance', valueAsNumber)
                      setDirtySafeDistance(true)
                    }
                  }}
                />
              </FormGroup>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Floor Plan CAD" required />
            </Details.Row>
            <Details.Files
              containerStyle={{
                border: errors.autocad_file ? '1px solid var(--red)' : '',
              }}
              editing
              maxSize={120}
              files={
                // only allowing 1 file at a time & local takes priority
                localMapCad ? [] : floor && assetVersion && imageAutoCad ? [imageAutoCad] : []
              }
              localFiles={localMapCad ? [localMapCad] : []}
              onAdd={(files) => {
                setLocalMapCad(files[0])
                setDirty(true)
                setErrors((errors) => ({
                  ...errors,
                  autocad_file: '',
                }))
              }}
              onRemove={
                localMapCad ? () => setLocalMapCad(null) : () => removeImageAndCad('autoCad')
              }
              fileTypes={{ 'image/*': ['.dwg'] }}
            />
            {errors.autocad_file && (
              <span style={{ color: 'var(--red)', fontSize: 12 }}>{errors.autocad_file}</span>
            )}
          </Details.Column>
        </Details.Columns>
        {showIntegrations && (
          <>
            <Details.Header iconName="puzzle-piece" label="Integrations Info" />
            <Details.Columns>
              <Details.Column>
                <Details.Row>
                  <Details.Label label="Meridian Map External ID" />
                  <FormGroup>
                    <InputGroup
                      value={meridianMap?.externalId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setDirty(true)
                        setMeridianMap({ externalId: e.target.value })
                      }}
                    />
                  </FormGroup>
                </Details.Row>
              </Details.Column>
            </Details.Columns>
          </>
        )}
        <Details.Footer>
          <Flex align="center" justify="flex-end">
            <Confirmation enabled={dirty} text="Discard unsaved changes?" onConfirm={onCancel}>
              <Button
                className="mr2"
                text="Cancel"
                onClick={() => {
                  if (!dirty) onCancel()
                }}
              />
            </Confirmation>
            <ButtonGroup>
              <Button
                disabled={!dirty}
                intent={Intent.PRIMARY}
                loading={loading}
                text="Save"
                type="submit"
              />
            </ButtonGroup>
          </Flex>
        </Details.Footer>
      </Details>
    </div>
  )
}

export function NewFloorForm({
  buildingId,
  onCancel,
  onSuccess,
  reload,
  onClose,
  dirty,
  setDirty,
}: {
  buildingId: string
  onCancel: () => void
  onSuccess: (floor: any, shouldCallback?: boolean) => any
  reload: () => void
  onClose: () => void
  dirty: boolean
  setDirty: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { integrations } = useAppData()
  const showIntegrations = integrations && meridianEnabled(integrations?.active)

  const newFloor = {
    name: '',
    story: 1,
    capacity: 0,
    code: '',
    areaSize: '',
    scale: 0,
    meridianMap: null,
    safeDistance: 0,
    assetVersion: {
      mapH: 0,
      mapW: 0,
    },
  }

  return (
    <FloorForm
      dirty={dirty}
      setDirty={setDirty}
      // @ts-ignore
      floor={newFloor}
      buildingId={buildingId}
      onCancel={onCancel}
      onSubmit={(floor, av, shouldCallback) =>
        api.building.floor
          // @ts-ignore
          // eslint-disable-next-line
          .create({ ...floor, building_id: buildingId })
          .then((floor: any) => {
            if (av) {
              api.building.floor.assets
                .create({
                  floorId: floor.id,
                  mapH: av ? av.mapH : 0,
                  mapW: av ? av.mapW : 0,
                  background: av ? av.background : undefined,
                  autocad: av ? av.autocad : undefined,
                })
                .then((newAssetVersion: any) =>
                  onSuccess({ ...floor, assetVersion: newAssetVersion }, shouldCallback)
                )
                .then(() => fetchMeta())
              return
            }
            onSuccess({ ...floor, assetVersion: null }, shouldCallback)
          })
      }
      reload={reload}
      onClose={onClose}
      showIntegrations={showIntegrations}
    />
  )
}
